'use client';

import { notFound } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { JourneyTopBarLoader } from '~/components/Journey/loaders/JourneyTopBarLoader';
import { useRouter } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundSkeletonTrips } from '~/stores/playgroundStore';

type Props = Readonly<{
  skeletonId: string;
}>;

export function JourneySkeletonResolver({ skeletonId }: Props) {
  const router = useRouter();
  const skeletonTrips = usePlaygroundSkeletonTrips();
  const playgroundContext = usePlaygroundContext();
  const { setFullTrip } = usePlaygroundActions();
  const buildJourneyFromSkeleton = api.playground.buildJourneyFromSkeleton.useMutation();

  const skeleton = useMemo(
    () => skeletonTrips.find((trip) => trip.uniqueId === skeletonId),
    [skeletonTrips, skeletonId]
  );

  const [hasFailed, setHasFailed] = useState(false);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (skeleton === undefined || hasStartedRef.current) return;
    hasStartedRef.current = true;

    const csUser = globalThis.window === undefined ? '' : (globalThis.sessionStorage?.getItem('csUser') ?? '');

    buildJourneyFromSkeleton
      .mutateAsync({
        csUser,
        playgroundInputContext: playgroundContext,
        skeletonTripId: skeleton.uniqueId
      })
      .then((newFullTrip) => {
        if (!newFullTrip?.items?.length) {
          setHasFailed(true);
          return;
        }
        setFullTrip(skeleton.uniqueId, newFullTrip);
        if (newFullTrip.uniqueId === skeleton.uniqueId) {
          router.refresh();
        } else {
          router.replace(`/journey/${newFullTrip.uniqueId}?source=playground`);
        }
      })
      .catch(() => {
        setHasFailed(true);
      });
  }, [skeleton, playgroundContext, buildJourneyFromSkeleton, setFullTrip, router]);

  if (skeleton === undefined || hasFailed) {
    notFound();
  }

  return <JourneyTopBarLoader />;
}
